import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";

import DrawerHeader from "@/components/userDrawer/DrawerHeader";
import DrawerStatusBar from "@/components/userDrawer/DrawerStatusBar";
import DrawerEditForm from "@/components/userDrawer/DrawerEditForm";
import DrawerPasswordReset from "@/components/userDrawer/DrawerPasswordReset";
import DrawerDangerZone from "@/components/userDrawer/DrawerDangerZone";

const formFromUser = (u) => ({
  name: u.name || "",
  role: u.role || "employee",
  company_id: u.company_id || "",
  active: u.active !== false,
  job_title: u.job_title || "",
});

/**
 * Right-side drawer for managing a single user from the Owner Console.
 * Props:
 *  - user: the user record to manage
 *  - companies: list of companies for the company dropdown
 *  - onClose(): close the drawer
 *  - onChanged(updatedOrNullOnDelete): notify parent to refresh / drop the row
 *  - currentOwnerId: the signed-in owner's id (used to disable self-destructive actions)
 */
export default function UserManageDrawer({ user, companies, onClose, onChanged, currentOwnerId }) {
  useBodyScrollLock(true);
  const [form, setForm] = useState(() => formFromUser(user));
  const [saving, setSaving] = useState(false);

  // Re-sync state when a different row is opened.
  useEffect(() => { setForm(formFromUser(user)); }, [user]);

  // ESC closes the drawer.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const isSelf = currentOwnerId === user.id;

  const patch = async (payload, successMsg) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/owner/users/${user.id}`, payload);
      if (successMsg) toast.success(successMsg);
      onChanged(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => patch(
    {
      name: form.name.trim(),
      role: form.role,
      company_id: form.company_id || "",
      active: form.active,
      job_title: form.job_title,
    },
    "User updated",
  );

  const handleToggleActive = async () => {
    const next = !form.active;
    const data = await patch({ active: next }, next ? "Account enabled" : "Account disabled");
    if (data) setForm((f) => ({ ...f, active: data.active !== false }));
  };

  const body = (
    <>
      <div
        data-testid="user-drawer-backdrop"
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm animate-in fade-in duration-150"
        onClick={onClose}
      />
      <aside
        data-testid="user-drawer"
        role="dialog"
        aria-modal="true"
        className="fixed right-0 top-0 bottom-0 z-[61] w-full sm:w-[460px] max-w-full overflow-y-auto border-l-4 shadow-2xl animate-in slide-in-from-right duration-200"
        style={{ backgroundColor: "#0B1E3F", borderLeftColor: "#F9D71C", color: "#fff" }}
      >
        <DrawerHeader user={user} onClose={onClose} />
        <DrawerStatusBar active={form.active} isSelf={isSelf} saving={saving} onToggle={handleToggleActive} />
        <DrawerEditForm
          user={user}
          form={form}
          setForm={setForm}
          isSelf={isSelf}
          saving={saving}
          companies={companies}
          onSave={handleSave}
        />
        <DrawerPasswordReset userId={user.id} />
        {!isSelf && <DrawerDangerZone user={user} onDeleted={() => onChanged(null)} />}
      </aside>
    </>
  );

  return createPortal(body, document.body);
}
